import { useState } from 'react';
import {
  TablePagination,
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  DialogContainer,
  DialogBody,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { PAGINATION_CONFIG } from '../../constants/statisticsConstants';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';

const COLUMN_DEFINITIONS = [
  { key: 'group', label: 'Grupo', isNumeric: false },
  { key: 'contract_number', label: 'N° contrato', isNumeric: false },
  { key: 'fee_number1', label: 'Cuota 1', isNumeric: true },
  { key: 'fee_number2', label: 'Cuota 2', isNumeric: true },
  { key: 'fee_amount', label: '$ cuota', isNumeric: true },
  { key: 'pei_amount', label: '$ PEI', isNumeric: true },
  { key: 'life_insurance_amount', label: '$ seg. vida', isNumeric: true },
  { key: 'fire_insurance_amount', label: '$ seg. incen.', isNumeric: true },
  { key: 'social_fee_amount', label: '$ cuota social', isNumeric: true },
  { key: 'real_estate_tax_amount', label: 'Imp. Inmo.', isNumeric: true },
  { key: 'first_due_amount', label: '$ 1° venc.', isNumeric: true },
  { key: 'second_due_amount', label: '$ 2° venc.', isNumeric: true },
  { key: 'third_due_amount', label: '$ 3° venc.', isNumeric: true },
];

const sanitizeNumericValue = (value) => {
  const parsed = Number(value ?? 0);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return Math.round(parsed);
};

const formatValueForDisplay = (column, value) => {
  if (column.isNumeric) {
    return sanitizeNumericValue(value).toString();
  }

  if (value === null || value === undefined) {
    return '';
  }

  return value.toString();
};

const buildCsvContent = (rows) => {
  const headerRow = COLUMN_DEFINITIONS.map((column) => column.label).join(';');

  const dataRows = rows.map((row) =>
    COLUMN_DEFINITIONS.map((column) =>
      column.isNumeric
        ? sanitizeNumericValue(row[column.key])
        : (row[column.key] ?? '')
    ).join(';')
  );

  return [headerRow, ...dataRows].join('\r\n');
};

const CouponDetailsFerModal = ({ open, handleClose, header, results }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE);

  if (!header || !results || results.length === 0) {
    return null;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToCsv = () => {
    const csvContent = buildCsvContent(results);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const exportDate = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `Emision_cupones(${exportDate}).csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const paginatedResults = results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Detalle cupones (Fer)"
        onExportExcel={handleExportToCsv}
        onExportPDF={() => {}}
        excelLabel="CSV"
        showPdfButton={false}
        onClose={handleClose}
      />
      <DialogBody>
        <Box
          sx={{ mb: 3, textAlign: 'center', border: '1px solid #4a90e2', p: 2, borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {header.constructora_info}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado a la fecha: {header.updated_at_date}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            {header.date_filter_text}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: '#555' }}>
          Cantidad de cupones: {results.length}
        </Typography>

        <GeneralTableBox>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMN_DEFINITIONS.map((column) => (
                  <HeadTableCell key={column.key}>{column.label}</HeadTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={`${result.contract_number}-${index}`}>
                  {COLUMN_DEFINITIONS.map((column) => (
                    <RowTableCell key={column.key}>
                      {formatValueForDisplay(column, result[column.key])}
                    </RowTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GeneralTableBox>

        <TablePagination
          component="div"
          count={results.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Cupones por página"
          rowsPerPageOptions={PAGINATION_CONFIG.ROWS_PER_PAGE_OPTIONS}
        />
      </DialogBody>
    </DialogContainer>
  );
};

export default CouponDetailsFerModal;
